import type {
  ArticleSearchCondition,
  ArticleStatus,
  IsThisInclude,
  LAPRS,
  NumberingMethod,
} from '@libs/enums';
import type {
  ItemAttributes,
  ItemNames,
  NumericRangeObject,
  ParentBranchObject,
  WithBlankSearchObject,
  WithExclusionObject,
} from '@ps-smart-cp/ppfw-client';
import {
  Bool,
  Characters,
  ItemName,
  Locale,
  Numeric,
  NumericRange,
  ParentBranch,
  SingleValue,
  ValueSet,
  WithBlankSearch,
  WithExclusion,
} from '@ps-smart-cp/ppfw-client';

interface Schema {
  /**
   * 物件番号
   */
  articleNo: ParentBranchObject;
  /**
   * 物件番号・親
   */
  articleNoParent: string;
  /**
   * 物件番号・枝
   */
  articleNoBranch: string;
  /**
   * 変更後物件番号・親
   */
  articleNoParentAfterChange: string;
  /**
   * 変更後物件番号・枝
   */
  articleNoBranchAfterChange: string;
  /**
   * リース料按分比率
   */
  leaseFeeDistributionRatio: string;
  /**
   * 物件価額
   */
  articleAmount: string;
  /**
   * 残価保証額
   */
  guaranteedResidualValue: string;
  /**
   * 残価保証額(抽出条件)
   */
  guaranteedResidualValueCondition: string;
  /**
   * リース物件ID
   */
  laArticleId: number | null;
  /**
   * 変更後物件番号
   */
  articleNoAfterChange: ParentBranchObject;
  /**
   * 物件名称
   */
  articleName: string;
  /**
   * 物件予備名称1
   */
  articleSpareName01: string;
  /**
   * 物件予備名称2
   */
  articleSpareName02: string;
  /**
   * 物件件数
   */
  articleCount: string;
  /**
   * 物件価額合計
   */
  totalArticleAmount: string;
  /**
   * 解約後物件価額合計
   */
  totalArticleAmountAfterCancel: string;
  /**
   * 残価保証額合計
   */
  totalGuaranteedResidualValue: string;
  /**
   * 解約後残価保証額合計
   */
  totalGuaranteedResidualValueAfterCancel: string;
  /**
   * 物件番号・親範囲(抽出条件)
   */
  articleNoParentRange: WithExclusionObject;
  /**
   * 一部の物件を解約する
   */
  isCancelPartialArticle: boolean;
  /**
   * 解約後見積現金購入額
   */
  estimateCashPurchaseAmountAfterCancel: string;
  /**
   * リース料按分比率合計
   */
  totalLeaseFeeDistributionRatio: string;
  /**
   * 解約後リース料按分比率合計
   */
  totalLeaseFeeDistributionRatioAfterCancel: string;
  /**
   * 購入金額
   */
  purchaseAmount: string;
  /**
   * 物件状態区分(抽出条件)
   */
  articleConditionClassCondition: Set<ArticleStatus>;
  /**
   * 物件状態区分
   */
  articleConditionClass: ArticleStatus;
  /**
   * 物件情報を自動で作成する
   */
  createArticleInfoAuto: boolean;
  /**
   * 作成件数
   */
  createNumber: string;
  /**
   * 物件番号(採番)
   */
  numberingMethod: NumberingMethod;
  /**
   * 物件番号(抽出条件)
   */
  articleNoRange: string;
  /**
   * 変更後物件番号(採番)
   */
  articleNoAfterChangeNumbering: NumberingMethod;
  /**
   * リース物件元本返済予定表の帳票種類
   */
  laArtPrinRepaySchedRptType: LAPRS;
  /**
   * 物件番号・親(抽出条件)
   */
  articleNoParentCondition: string;
  /**
   * 物件番号（グリッド用）
   */
  articleNoGrid: string;
  /**
   * 物件検索条件
   */
  articleSearchCondition: ArticleSearchCondition;
  /**
   * 物件予備名称1(抽出条件)
   */
  articleSpareName01Condition: WithBlankSearchObject;
  /**
   * 物件予備名称2(抽出条件)
   */
  articleSpareName02Condition: WithBlankSearchObject;
  /**
   * 物件価額(抽出条件)
   */
  articleAmountRange: NumericRangeObject;
  /**
   * 残価保証額(抽出条件)
   */
  guaranteedResidualValueRange: NumericRangeObject;
  /**
   * 更新中物件(抽出条件)
   */
  updatingArticlesCondition: IsThisInclude;
}

const attributes = {
  numberingMethod: SingleValue.shortValue(),
  articleNo: ParentBranch.of(
    Characters.preset.halfWidth({ minLen: 0, maxLen: 15, pad: { position: 'start', with: '0' } }),
    Characters.preset.numerical({ minLen: 0, maxLen: 3, pad: { position: 'start', with: '0' } }),
  ),
  articleNoParent: Characters.preset.halfWidth({
    minLen: 0,
    maxLen: 15,
    pad: { position: 'start', with: '0' },
  }),
  articleName: Characters.preset.freeFormat({ minLen: 0, maxLen: 100 }),
  articleSpareName01: Characters.preset.freeFormat({ minLen: 0, maxLen: 100 }),
  articleSpareName02: Characters.preset.freeFormat({ minLen: 0, maxLen: 100 }),
  leaseFeeDistributionRatio: Numeric.preset.currency(),
  articleAmount: Numeric.preset.currency(),
  guaranteedResidualValue: Numeric.preset.currency(),
  createNumber: Numeric.of(0, 999, 0, true),
  purchaseAmount: Numeric.preset.currency(),
  articleCount: Numeric.of(0, 999, 0),
  totalArticleAmount: Numeric.preset.currency(),
  totalGuaranteedResidualValue: Numeric.preset.currency(),
  totalLeaseFeeDistributionRatio: Numeric.preset.currency(),
  guaranteedResidualValueCondition: SingleValue.shortValue(),
  articleNoAfterChange: ParentBranch.of(
    Characters.preset.halfWidth({ minLen: 0, maxLen: 15, pad: { position: 'start', with: '0' } }),
    Characters.preset.numerical({ minLen: 0, maxLen: 3, pad: { position: 'start', with: '0' } }),
  ),
  totalArticleAmountAfterCancel: Numeric.preset.currency(),
  totalGuaranteedResidualValueAfterCancel: Numeric.preset.currency(),
  articleNoParentRange: WithExclusion.of(Characters.preset.halfWidth({ maxLen: 250 })),
  isCancelPartialArticle: Bool.new(),
  estimateCashPurchaseAmountAfterCancel: Numeric.preset.currency(),
  totalLeaseFeeDistributionRatioAfterCancel: Numeric.preset.currency(),
  articleConditionClassCondition: ValueSet.new(),
  createArticleInfoAuto: Bool.new(),
  articleNoRange: Characters.preset.halfWidth({ maxLen: 99999 }),
  articleNoAfterChangeNumbering: SingleValue.shortValue(),
  laArtPrinRepaySchedRptType: SingleValue.shortValue(true),
  articleNoParentCondition: Characters.preset.freeFormat({ maxLen: 15 }),
  articleNoGrid: Characters.preset.freeFormat(),
  articleSearchCondition: SingleValue.longValue(),
  articleSpareName01Condition: WithBlankSearch.of(Characters.preset.freeFormat({ maxLen: 100 })),
  articleSpareName02Condition: WithBlankSearch.of(Characters.preset.freeFormat({ maxLen: 100 })),
  articleAmountRange: NumericRange.of(0, 9_999_999_999_999, 0),
  guaranteedResidualValueRange: NumericRange.of(0, 9_999_999_999_999, 0),
  updatingArticlesCondition: SingleValue.longValue(),
} satisfies ItemAttributes<Omit<Schema, 'laArticleId'>>;

interface Record {
  articleNo: string;
  articleNoParent: string;
  articleNoBranch: string;
  leaseFeeDistributionRatio: string;
  articleAmount: string;
  guaranteedResidualValue: string;
  guaranteedResidualValueCondition: string;
  laArticleId: number | null;
  articleNoParentAfterChange: string;
  articleNoBranchAfterChange: string;
  articleName: string;
  articleSpareNameOne: string;
  articleSpareNameTwo: string;
  spareName01: string;
  spareName02: string;
  amount: string;
  name: string;
  articleCount: string;
  totalArticleAmount: string;
  totalArticleAmountAfterCancel: string;
  totalGuaranteedResidualValue: string;
  totalGuaranteedResidualValueAfterCancel: string;
  articleNoParentRange: string;
  isArticleNoParentRangeExcluded: boolean;
  isCancelPartialArticle: boolean;
  estimateCashPurchaseAmountAfterCancel: string;
  totalLeaseFeeDistributionRatio: string;
  totalLeaseFeeDistributionRatioAfterCancel: string;
  purchaseAmount: string;
  articleConditionClassCondition: string[];
  articleConditionClass: string;
  createArticleInfoAuto: boolean;
  createNumber: string;
  numberingMethod: string;
  articleNoRange: string;
  articleNoAfterChangeNumbering: string;
  laArtPrinRepaySchedRptType: string;
  articleNoParentCondition: string;
  articleSearch: string;
  isArticleSpareNameOneBlankOnly: boolean;
  isArticleSpareNameTwoBlankOnly: boolean;
  articleAmountFrom: string;
  articleAmountTo: string;
  guaranteedResidualValueFrom: string;
  guaranteedResidualValueTo: string;
  updatingArticles: string;
}

const names = {
  articleNo: ItemName.of([Locale.ja, '物件番号'], [Locale.en, 'Article No']),
  articleNoParent: ItemName.of([Locale.ja, '物件番号・親'], [Locale.en, 'Article No Parent']),
  articleNoBranch: ItemName.of([Locale.ja, '物件番号・枝'], [Locale.en, 'Article No Branch']),
  articleNoParentAfterChange: ItemName.of([Locale.ja, '変更後物件番号・親']),
  articleNoBranchAfterChange: ItemName.of([Locale.ja, '変更後物件番号・枝']),
  leaseFeeDistributionRatio: ItemName.of(
    [Locale.ja, 'リース料按分比率'],
    [Locale.en, 'Lease Fee Distribution Ratio'],
  ),
  articleAmount: ItemName.of([Locale.ja, '物件価額'], [Locale.en, 'Article Amount']),
  guaranteedResidualValue: ItemName.of([Locale.ja, '残価保証額'], [Locale.en, 'Guaranteed Residual Value']),
  guaranteedResidualValueCondition: ItemName.of(
    [Locale.ja, '残価保証額'],
    [Locale.en, 'Guaranteed Residual Value Condition'],
  ),
  articleNoAfterChange: ItemName.of([Locale.ja, '変更後物件番号'], [Locale.en, 'Article No After Change']),
  articleName: ItemName.of([Locale.ja, '物件名称'], [Locale.en, 'Article Name']),
  articleSpareName01: ItemName.of([Locale.ja, '物件予備名称1'], [Locale.en, 'Article Spare Name One']),
  articleSpareName02: ItemName.of([Locale.ja, '物件予備名称2'], [Locale.en, 'Article Spare Name Two']),
  articleCount: ItemName.of([Locale.ja, '物件件数'], [Locale.en, 'Article Count']),
  totalArticleAmount: ItemName.of([Locale.ja, '物件価額合計'], [Locale.en, 'Total Article Amount']),
  totalArticleAmountAfterCancel: ItemName.of(
    [Locale.ja, '解約後物件価額合計'],
    [Locale.en, 'Total Article Amount After Cancel'],
  ),
  totalGuaranteedResidualValue: ItemName.of(
    [Locale.ja, '残価保証額合計'],
    [Locale.en, 'Total Guaranteed Residual Value'],
  ),
  totalGuaranteedResidualValueAfterCancel: ItemName.of(
    [Locale.ja, '解約後残価保証額合計'],
    [Locale.en, 'Total Guaranteed Residual Value After Cancel'],
  ),
  articleNoParentRange: ItemName.of([Locale.ja, '物件番号・親範囲'], [Locale.en, 'Article No Parent Range']),
  isCancelPartialArticle: ItemName.of(
    [Locale.ja, '一部の物件を解約する'],
    [Locale.en, 'Cancel Partial Article'],
  ),
  estimateCashPurchaseAmountAfterCancel: ItemName.of(
    [Locale.ja, '解約後見積現金購入額'],
    [Locale.en, 'Estimate Cash Purchase Amount After Cancel'],
  ),
  totalLeaseFeeDistributionRatio: ItemName.of(
    [Locale.ja, 'リース料按分比率合計'],
    [Locale.en, 'Total Lease Fee Distribution Ratio'],
  ),
  totalLeaseFeeDistributionRatioAfterCancel: ItemName.of(
    [Locale.ja, '解約後リース料按分比率合計'],
    [Locale.en, 'Total Lease Fee Distribution Ratio After Cancel'],
  ),
  purchaseAmount: ItemName.of([Locale.ja, '購入金額'], [Locale.en, 'Purchase Amount']),
  articleConditionClassCondition: ItemName.of(
    [Locale.ja, '物件状態区分'],
    [Locale.en, 'Article Condition Class'],
  ),
  articleConditionClass: ItemName.of([Locale.ja, '物件状態区分'], [Locale.en, 'Article Condition Class']),
  createArticleInfoAuto: ItemName.of([Locale.ja, '物件情報を自動で作成する']),
  createNumber: ItemName.of([Locale.ja, '作成件数']),
  numberingMethod: ItemName.of([Locale.ja, '物件番号採番方法']),
  articleNoRange: ItemName.of([Locale.ja, '物件番号']),
  articleNoAfterChangeNumbering: ItemName.of([Locale.ja, '変更後物件番号採番方法']),
  laArtPrinRepaySchedRptType: ItemName.of([Locale.ja, '帳票種類']),
  articleNoParentCondition: ItemName.of([Locale.ja, '物件番号・親']),
  articleNoGrid: ItemName.of([Locale.ja, '物件番号']),
  articleSearchCondition: ItemName.of([Locale.ja, '物件検索条件'], [Locale.en, 'Article Search Conditions']),
  updatingArticlesCondition: ItemName.of([Locale.ja, '更新中物件'], [Locale.en, 'Updating Articles']),
} satisfies ItemNames<Omit<Schema, 'laArticleId'>>;

const labelNames = {
  articleCreationDialog: ItemName.of([Locale.ja, '物件-作成ダイアログ']),
  laArticleInfoChangeImport: ItemName.of(
    [Locale.ja, 'リース物件情報変更(取込)'],
    [Locale.en, 'La Article Info Change Import.'],
  ),
  laArticleInfoChangeImportConfirm: ItemName.of(
    [Locale.ja, 'リース物件情報変更(取込)確認'],
    [Locale.en, 'La Article Info Change Import Confirm.'],
  ),
  changeInfo: ItemName.of([Locale.ja, '変更情報']),
  articleCancellationInfo: ItemName.of([Locale.ja, '物件解約情報']),
  articleInfo: ItemName.of([Locale.ja, '物件情報']),
  articleAndAssetInfo: ItemName.of([Locale.ja, '物件・資産情報']),
  referenceLaArticleRegistrationHistory: ItemName.of([Locale.ja, 'リース物件登録履歴照会']),
  laArticleInfoChange: ItemName.of([Locale.ja, 'リース物件情報変更'], [Locale.en, 'La Article Info Change']),
  laArticleLedgerDetails: ItemName.of(
    [Locale.ja, 'リース物件台帳(1件別明細)'],
    [Locale.en, 'La Article Ledger Details'],
  ),
  leaseContractGuaranteedResidualValue: ItemName.of([Locale.ja, '残価保証額(リース契約)']),
};

const titleNames = {
  laArticleListSearchTitle: ItemName.of(
    [Locale.ja, 'リース物件一覧照会'],
    [Locale.en, 'La Article List Search'],
  ),
  laArticleLeaseFeePaymentList: ItemName.of(
    [Locale.ja, 'リース物件リース料支払リスト'],
    [Locale.en, 'La Article Lease Fee Payment List'],
  ),
};

const laArticle = {
  names,
  labelNames,
  titleNames,
  attributes,
};

interface LaArticle {
  schema: Schema;
  record: Record;
}

export type { LaArticle };
export { laArticle };
